import { useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { GLASS_GROUPS } from "@/lib/pricing";
import { NUM_ERR, validNum } from "@/components/settings/PricingSection";
import { slug } from "@/components/quote/GlassFields";

const inputCls = (err) =>
  `h-9 w-full rounded-md border bg-slate-900/70 px-3 text-sm text-slate-100 outline-none focus:border-amber-500/70 ${err ? "border-red-500/70" : "border-slate-800"}`;

function OptionList({ group, label, rows, errors, onChange }) {
  const set = (i, p) => onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <div data-testid={`glass-group-${group}`} className="rounded-xl border border-slate-800 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_110px_32px] items-start gap-2">
            <div>
              <input data-testid={`glass-${group}-name-input-${i}`} value={r.name} onChange={(e) => set(i, { name: e.target.value })}
                className={inputCls(errors[`${group}.${i}.name`])} />
              {errors[`${group}.${i}.name`] && <p className="mt-1 text-[11px] text-red-400">{errors[`${group}.${i}.name`]}</p>}
            </div>
            <div>
              <div className="relative">
                <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-slate-500">+$</span>
                <input data-testid={`glass-${group}-cost-input-${i}`} inputMode="decimal" value={r.cost} onChange={(e) => set(i, { cost: e.target.value })}
                  className={`${inputCls(errors[`${group}.${i}.cost`])} pl-6 text-right font-mono-num`} />
              </div>
              {errors[`${group}.${i}.cost`] && <p data-testid={`glass-${group}-cost-error-${i}`} className="mt-1 text-[11px] text-red-400">{errors[`${group}.${i}.cost`]}</p>}
            </div>
            <button data-testid={`glass-${group}-delete-button-${i}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} title="Remove"
              className="mt-1 rounded-md p-1.5 text-slate-500 transition-colors hover:bg-red-500/10 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button data-testid={`glass-${group}-add-button`} onClick={() => onChange([...rows, { name: "", cost: "0" }])}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-amber-300 transition-colors hover:bg-amber-500/10">
        <Plus className="h-3.5 w-3.5" /> Add option
      </button>
    </div>
  );
}

export function GlassSection({ company, onChange }) {
  const [opts, setOpts] = useState({});
  const [arch, setArch] = useState("0");
  const [grid, setGrid] = useState({ GBG: "0", SDL: "0", TDL: "0" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const src = company.glass_options || {};
    setOpts(Object.fromEntries(GLASS_GROUPS.map(([g]) => [g, (src[g] || []).map((o) => ({ name: o.name, cost: String(o.cost ?? 0) }))])));
    setArch(String(company.arch_surcharge ?? 0));
    setGrid(Object.fromEntries(["GBG", "SDL", "TDL"].map((k) => [k, String(company.grid_costs?.[k] ?? 0)])));
  }, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const errs = {};
    GLASS_GROUPS.forEach(([g]) => {
      const seen = new Set();
      (opts[g] || []).forEach((o, i) => {
        const n = o.name.trim().toLowerCase();
        if (!n) errs[`${g}.${i}.name`] = "Name is required";
        else if (seen.has(n)) errs[`${g}.${i}.name`] = "Duplicate name";
        seen.add(n);
        if (!validNum(o.cost)) errs[`${g}.${i}.cost`] = NUM_ERR;
      });
    });
    if (!validNum(arch)) errs.arch = NUM_ERR;
    Object.entries(grid).forEach(([k, v]) => { if (!validNum(v)) errs[`grid.${k}`] = NUM_ERR; });
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error("Fix the highlighted fields. Nothing was saved.");
    setSaving(true);
    try {
      const glass_options = Object.fromEntries(GLASS_GROUPS.map(([g]) => [g, (opts[g] || []).map((o) => ({ name: o.name.trim(), cost: Number(o.cost) }))]));
      const { data } = await api.put("/settings/glass", { glass_options, arch_surcharge: Number(arch),
        grid_costs: Object.fromEntries(Object.entries(grid).map(([k, v]) => [k, Number(v)])) });
      onChange(data);
      toast.success("Glass & arch options saved");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-800 bg-[#F2F2F7]/60 p-6" data-testid="settings-glass-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-base font-bold text-slate-100 md:text-lg">Glass, glazing & arch options</h2>
          <p className="mt-1 text-sm text-slate-400">Each option adds its cost per sq ft to a line's cost. Never shown on the estimate.</p>
        </div>
        <div className="w-44">
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Arch surcharge / sq ft</label>
          <input data-testid="settings-arch-surcharge-input" inputMode="decimal" value={arch} onChange={(e) => setArch(e.target.value)}
            className={`${inputCls(errors.arch)} text-right font-mono-num`} />
          {errors.arch && <p data-testid="settings-arch-surcharge-error" className="mt-1 text-[11px] text-red-400">{errors.arch}</p>}
        </div>
      </div>
      <div className="mt-6 rounded-xl border border-slate-800 p-4" data-testid="glass-group-grids">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Grid type cost / sq ft</p>
        <div className="grid grid-cols-3 gap-3">
          {["GBG", "SDL", "TDL"].map((k) => (
            <label key={k} className="block">
              <span className="mb-1 block text-[11px] text-slate-500">{k}</span>
              <input data-testid={`settings-grid-cost-${k.toLowerCase()}-input`} inputMode="decimal" value={grid[k]}
                onChange={(e) => setGrid((g) => ({ ...g, [k]: e.target.value }))} className={`${inputCls(errors[`grid.${k}`])} text-right font-mono-num`} />
              {errors[`grid.${k}`] && <span data-testid={`settings-grid-cost-${k.toLowerCase()}-error`} className="mt-1 block text-[11px] text-red-400">{errors[`grid.${k}`]}</span>}
            </label>
          ))}
        </div>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {GLASS_GROUPS.map(([g, label]) => (
          <OptionList key={g} group={g} label={label} rows={opts[g] || []} errors={errors}
            onChange={(rows) => setOpts((s) => ({ ...s, [g]: rows }))} />
        ))}
      </div>
      <button data-testid="save-glass-settings-button" onClick={save} disabled={saving}
        className="mt-6 inline-flex h-10 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save glass & arch options
      </button>
    </section>
  );
}

export { slug };
